import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapView } from './MapView';
import { SchoolPanel } from './SchoolPanel';
import { Controls } from './Controls';
import { EnrollmentChart } from './EnrollmentChart';
import { AreaReassignModal } from './AreaReassignModal';
import {
  schoolNames,
  computeAssignments,
  computeMinDistanceAssignments,
  computeCurrentBoundaryAssignments,
} from './data';
import type { MapState, ViewMode, YearKey } from './types';

const STORAGE_KEY = 'djusd-shutdown-map-saved';

function getInitialState(): MapState {
  const schoolStatus: Record<string, boolean> = {};
  for (const name of schoolNames) {
    schoolStatus[name] = true;
  }

  return {
    schoolStatus,
    areaAssignments: computeCurrentBoundaryAssignments(),
    viewMode: 'zones',
    selectedYear: '2026',
  };
}

export function ShutdownImpactMap() {
  const [state, setState] = useState<MapState>(getInitialState);
  const [reassignArea, setReassignArea] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  // Toggle school on/off - mirrors VBA SchoolClick
  const handleToggleSchool = useCallback(
    (schoolName: string) => {
      setState((prev) => {
        const newStatus = { ...prev.schoolStatus };
        newStatus[schoolName] = !newStatus[schoolName];

        // Must have at least one school open
        const openCount = Object.values(newStatus).filter(Boolean).length;
        if (openCount === 0) {
          return prev;
        }

        window.BayCrewAnalytics?.track('school_toggled', {
          school: schoolName,
          action: newStatus[schoolName] ? 'opened' : 'closed',
        });

        const newAssignments = computeAssignments(newStatus);

        return {
          ...prev,
          schoolStatus: newStatus,
          areaAssignments: newAssignments,
        };
      });
    },
    [],
  );

  // Area click - show reassignment modal
  const handleAreaClick = useCallback((areaId: string) => {
    setReassignArea(areaId);
  }, []);

  // Reassign area to chosen school - mirrors VBA SchoolChange
  const handleReassign = useCallback(
    (schoolName: string) => {
      if (!reassignArea) return;
      setState((prev) => ({
        ...prev,
        areaAssignments: {
          ...prev.areaAssignments,
          [reassignArea]: schoolName,
        },
      }));
      setReassignArea(null);
    },
    [reassignArea],
  );

  // View mode change
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    window.BayCrewAnalytics?.track('view_mode_changed', { mode });
    setState((prev) => ({ ...prev, viewMode: mode }));
  }, []);

  // Year change
  const handleYearChange = useCallback((year: YearKey) => {
    setState((prev) => ({ ...prev, selectedYear: year }));
  }, []);

  // Preset: current boundaries - mirrors VBA currentboundaries
  const handlePreset = useCallback(
    (preset: 'current' | 'minimum-distance') => {
      window.BayCrewAnalytics?.track('preset_applied', { preset });
      setState((prev) => {
        const newStatus = { ...prev.schoolStatus };
        // Reset all schools to open for presets
        for (const name of schoolNames) {
          newStatus[name] = true;
        }

        const newAssignments =
          preset === 'current'
            ? computeCurrentBoundaryAssignments()
            : computeMinDistanceAssignments(newStatus);

        return {
          ...prev,
          schoolStatus: newStatus,
          areaAssignments: newAssignments,
        };
      });
    },
    [],
  );

  // Save/Load - mirrors VBA SaveMap/LoadMap
  const handleSave = useCallback(() => {
    const saved = {
      schoolStatus: state.schoolStatus,
      areaAssignments: state.areaAssignments,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    showToast('Map configuration saved');
  }, [state.schoolStatus, state.areaAssignments, showToast]);

  const handleLoad = useCallback(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      showToast('No saved configuration found');
      return;
    }
    try {
      const saved = JSON.parse(raw);
      setState((prev) => ({
        ...prev,
        schoolStatus: saved.schoolStatus,
        areaAssignments: saved.areaAssignments,
      }));
      showToast('Map configuration loaded');
    } catch {
      showToast('Failed to load saved configuration');
    }
  }, [showToast]);

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="bg-davis-navy text-white px-4 py-2.5 flex items-center justify-between flex-shrink-0 z-10 shadow-md">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="flex items-center gap-3 min-w-0 group">
            <svg viewBox="0 0 24 32" className="w-6 h-8 text-davis-gold flex-shrink-0 group-hover:scale-110 transition-transform" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" />
              <circle cx="12" cy="11" r="5" fill="white" opacity="0.9" />
            </svg>
            <div className="min-w-0">
              <h1 className="text-base font-display leading-tight group-hover:text-davis-gold transition-colors">
                DJUSD School Shutdown Impact Map
              </h1>
            <p className="text-[11px] text-white/60 leading-snug">
              Data from{' '}
              <a href="https://www.mgt.us/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white underline">MGT</a>
              {' '}&middot; Original VBA model by{' '}
              <a href="mailto:briancrussell@gmail.com" className="text-white/80 hover:text-white underline">Brian Russell</a>
            </p>
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <a
            href="https://baycrewtech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-davis-gold/15 border border-davis-gold/40 rounded-lg px-3 py-1.5 hover:bg-davis-gold/25 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-davis-gold" fill="currentColor">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12 2 L14 10 L12 8 L10 10 Z" />
              <path d="M12 22 L10 14 L12 16 L14 14 Z" />
              <path d="M2 12 L10 10 L8 12 L10 14 Z" />
              <path d="M22 12 L14 14 L16 12 L14 10 Z" />
            </svg>
            <span className="text-sm font-display font-bold tracking-wide text-davis-gold">Built by Bay Crew Tech</span>
            <svg className="w-3.5 h-3.5 text-davis-gold/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-4.5-6h6m0 0v6m0-6L9.75 14.25" />
            </svg>
          </a>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Map */}
        <div className="flex-1 relative">
          <MapView
            state={state}
            onAreaClick={handleAreaClick}
            onSchoolClick={handleToggleSchool}
          />

          {/* Instructions overlay */}
          <div className="absolute top-3 left-3 z-[500] bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg max-w-xs pointer-events-none">
            <p className="text-xs text-davis-slate">
              Click a <strong>school marker</strong> to toggle it on/off.
              Click an <strong>area</strong> to reassign it to a different school.
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'w-80' : 'w-0'
          } transition-all duration-300 flex-shrink-0 overflow-hidden border-l border-gray-200 bg-gray-50 absolute md:relative right-0 top-0 bottom-0 z-[600] md:z-0`}
        >
          <div className="w-80 h-full overflow-y-auto p-4 space-y-3">
            <Controls
              viewMode={state.viewMode}
              selectedYear={state.selectedYear}
              onViewModeChange={handleViewModeChange}
              onYearChange={handleYearChange}
              onPreset={handlePreset}
              onSave={handleSave}
              onLoad={handleLoad}
            />

            <hr className="border-gray-200" />

            <SchoolPanel
              state={state}
              onToggleSchool={handleToggleSchool}
            />

            <hr className="border-gray-200" />

            <EnrollmentChart state={state} />
          </div>
        </div>
      </div>

      {/* Area Reassignment Modal */}
      {reassignArea && (
        <AreaReassignModal
          areaId={reassignArea}
          currentSchool={state.areaAssignments[reassignArea] ?? ''}
          schoolStatus={state.schoolStatus}
          onSelect={handleReassign}
          onClose={() => setReassignArea(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[3000] bg-davis-charcoal text-white px-4 py-2 rounded-lg shadow-xl text-sm font-medium animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
